import { FREE_TEXT_QUESTION_TYPES } from '../types/assessmentQuestion';

import type { ActiveQuestion } from './resolveQuestionChoices';

/**
 * Pure, question-shape-driven render/validation switches, derived the same
 * way whether one question is on screen (today) or every question of a
 * `singleScreen` node is (grouped mode) — shared by `AssessmentContent`'s
 * validation branch and `QuestionField`'s render branch so the two can never
 * disagree about what kind of input a question is.
 */
export type QuestionFlags = {
  isMultiSelect: boolean;
  hasOtherItem: boolean;
  isDate: boolean;
  isComment: boolean;
  isFreeText: boolean;
  isNumericFreeText: boolean;
  isRepeatingList: boolean;
  isTelFreeText: boolean;
  isOptional: boolean;
};

export function getQuestionFlags(question: ActiveQuestion): QuestionFlags {
  const isMultiSelect = question.isMultiSelect === true;
  return {
    isMultiSelect,
    hasOtherItem: isMultiSelect && question.showOtherItem === true,
    isDate: question.questionType === 'date',
    isComment: question.questionType === 'comment',
    isFreeText: FREE_TEXT_QUESTION_TYPES.has(question.questionType ?? ''),
    isNumericFreeText: question.inputType === 'number' || question.questionType === 'number',
    isRepeatingList: question.questionType === 'repeatingList',
    isTelFreeText: question.inputType === 'tel',
    // Explicitly `false` → skippable. Absent defaults to required, so the legacy
    // screeners (which never set the flag) keep their behavior.
    isOptional: question.isRequired === false,
  };
}

/** Whether `value` names a choice authored with `exclusive: true` on `question`
 * (e.g. health_conditions' "None of these conditions"). Only choice questions
 * carry `exclusive`, so this is vacuously false for every other question type. */
export function isExclusiveChoiceValue(question: ActiveQuestion, value: string): boolean {
  return question.choices.some((choice) => choice.value === value && choice.exclusive === true);
}

// Human-friendly testID segment derived from a choice's display label:
// lowercased, non-alphanumeric runs collapsed to single hyphens, trimmed
// (e.g. "More than half the days" → "more-than-half-the-days", "Female" →
// "female").
export const slugify = (label: string) =>
  label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
